import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
// import "./Styles/InvUI.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt,
    faFileInvoice, faBox, faRupeeSign, faTag, faBalanceScale, faDollarSign,
    faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch,
    faUpload, faReceipt, faChevronDown, faChevronUp, faBoxes, faBarcode,
    faFileAlt, faLayerGroup, faCubes, faBoxOpen, faPallet, faInfoCircle,
    faToggleOn, faToggleOff, faCopy, faList, faStore, faEnvelope, faBuilding,
    faAddressCard, faIdCard, faCreditCard, faMoneyBill, faUniversity, faWallet,
    faPercent, faDownload, faEdit, faChartLine, faTimesCircle, faShoppingBag,
    faHistory, faUndo, faRedo, faPrint, faFilePdf, faTrashAlt
} from '@fortawesome/free-solid-svg-icons';

const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const PRODUCT_API = `${API_BASE}/mechanics_products.php`;

// ==================== PACKING TYPES CONFIGURATION ====================
const packingTypesList = [
    { value: '', label: 'Select Packing Type' },
    { value: 'box', label: 'Box', icon: faBox },
    { value: 'carton', label: 'Carton', icon: faBoxes },
    { value: 'crate', label: 'Crate', icon: faCubes },
    { value: 'packet', label: 'Packet', icon: faBoxOpen },
    { value: 'bundle', label: 'Bundle', icon: faLayerGroup },
    { value: 'bottle', label: 'Bottle', icon: faBox },
    { value: 'can', label: 'Can', icon: faBox }
];

const subPackingTypesMap = {
    box: [
        { value: 'bottles', label: 'Bottles', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 50 },
        { value: 'cans', label: 'Cans', defaultQty: 12 },
        { value: 'packs', label: 'Packs', defaultQty: 10 }
    ],
    carton: [
        { value: 'boxes', label: 'Boxes', defaultQty: 6 },
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'bottles', label: 'Bottles', defaultQty: 48 }
    ],
    crate: [
        { value: 'bottles', label: 'Bottles', defaultQty: 12 },
        { value: 'cans', label: 'Cans', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 30 }
    ],
    packet: [
        { value: 'pieces', label: 'Pieces', defaultQty: 20 },
        { value: 'grams', label: 'Grams', defaultQty: 500 }
    ],
    bundle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'dozen', label: 'Dozen', defaultQty: 10 }
    ],
    bottle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 }
    ],
    can: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 }
    ]
};

const unitsOfMeasure = [
    { value: '', label: 'Select Unit' },
    { value: 'liter_1', label: '1 Liter (1L)' },
    { value: 'liter_2', label: '2 Liters (2L)' },
    { value: 'liter_3', label: '3 Liters (3L)' },
    { value: 'liter_5', label: '5 Liters (5L)' },
    { value: 'milliliter', label: 'Milliliter (ml)' },
    { value: 'kilogram', label: 'Kilogram (kg)' },
    { value: 'gram', label: 'Gram (g)' },
    { value: 'piece', label: 'Piece (pc)' },
    { value: 'dozen', label: 'Dozen (dz)' },
    { value: 'box', label: 'Box' },
    { value: 'packet', label: 'Packet' },
    { value: 'can', label: 'Can' },
    { value: 'bottle', label: 'Bottle' }
];

// ==================== TRANSLATOR ====================
const createPurchasesTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_purchases_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Purchases Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_purchases_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'New Purchase': 'نیا خریداری',
                'Edit Purchase': 'خریداری میں ترمیم کریں',
                'Purchase Details': 'خریداری کی تفصیلات',
                'Bill Number': 'بل نمبر',
                'Supplier': 'سپلائر',
                'Supplier Name': 'سپلائر کا نام',
                'Phone': 'فون نمبر',
                'Address': 'پتہ',
                'Date': 'تاریخ',
                'Bill File': 'بل فائل',
                'Items': 'آئٹمز',
                'Add Item': 'آئٹم شامل کریں',
                'Product Name': 'پروڈکٹ کا نام',
                'Product Code': 'پروڈکٹ کوڈ',
                'Batch Number': 'بیچ نمبر',
                'Price': 'قیمت',
                'Quantity': 'مقدار',
                'Total': 'کل',
                'Remove': 'ہٹائیں',
                'Calculations': 'حسابات',
                'Subtotal': 'ذیلی کل',
                'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم',
                'Balance': 'باقی رقم',
                'Save Purchase': 'خریداری محفوظ کریں',
                'Update Purchase': 'خریداری اپ ڈیٹ کریں',
                'Clear': 'صاف کریں',
                'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس',
                'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'Clear Form': 'فارم صاف کریں',
                'View Drafts': 'ڈرافٹس دیکھیں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Search supplier...': 'سپلائر تلاش کریں...',
                'No suppliers found': 'کوئی سپلائر نہیں ملا',
                'Category': 'کیٹیگری',
                'Company': 'کمپنی',
                'Packing Type': 'پیکنگ کی قسم',
                'Sub Packing Type': 'ذیلی پیکنگ کی قسم',
                'Items/Pack': 'آئٹمز/پیک',
                'Unit of Measure': 'پیمائش کی اکائی',
                'Cost Per Pack': 'فی پیک لاگت',
                'Optional': 'اختیاری',
                'Browse': 'براؤز کریں',
                'Generate': 'جنریٹ کریں',
                'No file chosen': 'کوئی فائل منتخب نہیں',
                'Search Products': 'پروڈکٹس تلاش کریں'
            },
            'ar': {
                'New Purchase': 'شراء جديد',
                'Edit Purchase': 'تعديل الشراء',
                'Purchase Details': 'تفاصيل الشراء',
                'Bill Number': 'رقم الفاتورة',
                'Supplier': 'المورد',
                'Supplier Name': 'اسم المورد',
                'Phone': 'رقم الهاتف',
                'Address': 'العنوان',
                'Date': 'التاريخ',
                'Bill File': 'ملف الفاتورة',
                'Items': 'العناصر',
                'Add Item': 'إضافة عنصر',
                'Product Name': 'اسم المنتج',
                'Product Code': 'رمز المنتج',
                'Batch Number': 'رقم الدفعة',
                'Price': 'السعر',
                'Quantity': 'الكمية',
                'Total': 'المجموع',
                'Remove': 'إزالة',
                'Calculations': 'الحسابات',
                'Subtotal': 'المجموع الفرعي',
                'Grand Total': 'المجموع الكلي',
                'Paid Amount': 'المبلغ المدفوع',
                'Balance': 'الرصيد',
                'Save Purchase': 'حفظ الشراء',
                'Update Purchase': 'تحديث الشراء',
                'Clear': 'مسح',
                'Save Draft': 'حفظ المسودة',
                'Drafts': 'المسودات',
                'Saved Drafts': 'المسودات المحفوظة',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'Clear Form': 'مسح النموذج',
                'View Drafts': 'عرض المسودات',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Search supplier...': 'البحث عن مورد...',
                'No suppliers found': 'لم يتم العثور على موردين',
                'Category': 'الفئة',
                'Company': 'الشركة',
                'Packing Type': 'نوع التعبئة',
                'Sub Packing Type': 'نوع التعبئة الفرعي',
                'Items/Pack': 'العناصر/العبوة',
                'Unit of Measure': 'وحدة القياس',
                'Cost Per Pack': 'التكلفة لكل عبوة',
                'Optional': 'اختياري',
                'Browse': 'تصفح',
                'Generate': 'إنشاء',
                'No file chosen': 'لم يتم اختيار ملف',
                'Search Products': 'البحث عن منتجات'
            },
            'es': {
                'New Purchase': 'Compra Nueva',
                'Edit Purchase': 'Editar Compra',
                'Purchase Details': 'Detalles de Compra',
                'Bill Number': 'Número de Factura',
                'Supplier': 'Proveedor',
                'Supplier Name': 'Nombre del Proveedor',
                'Phone': 'Teléfono',
                'Address': 'Dirección',
                'Date': 'Fecha',
                'Bill File': 'Archivo de Factura',
                'Items': 'Elementos',
                'Add Item': 'Agregar Elemento',
                'Product Name': 'Nombre del Producto',
                'Product Code': 'Código de Producto',
                'Batch Number': 'Número de Lote',
                'Price': 'Precio',
                'Quantity': 'Cantidad',
                'Total': 'Total',
                'Remove': 'Eliminar',
                'Calculations': 'Cálculos',
                'Subtotal': 'Subtotal',
                'Grand Total': 'Total General',
                'Paid Amount': 'Monto Pagado',
                'Balance': 'Saldo',
                'Save Purchase': 'Guardar Compra',
                'Update Purchase': 'Actualizar Compra',
                'Clear': 'Limpiar',
                'Save Draft': 'Guardar Borrador',
                'Drafts': 'Borradores',
                'Saved Drafts': 'Borradores Guardados',
                'Draft loaded': 'Borrador cargado',
                'Draft cleared': 'Borrador limpiado',
                'Clear Form': 'Limpiar Formulario',
                'View Drafts': 'Ver Borradores',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Search supplier...': 'Buscar proveedor...',
                'No suppliers found': 'No se encontraron proveedores',
                'Category': 'Categoría',
                'Company': 'Compañía',
                'Packing Type': 'Tipo de Empaque',
                'Sub Packing Type': 'Tipo de Subempaque',
                'Items/Pack': 'Elementos/Paquete',
                'Unit of Measure': 'Unidad de Medida',
                'Cost Per Pack': 'Costo por Paquete',
                'Optional': 'Opcional',
                'Browse': 'Examinar',
                'Generate': 'Generar',
                'No file chosen': 'No se eligió archivo',
                'Search Products': 'Buscar Productos'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addPurchase: 'New Purchase',
        editPurchase: 'Edit Purchase',
        purchaseDetails: 'Purchase Details',
        billNumber: 'Bill Number',
        supplier: 'Supplier',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        address: 'Address',
        date: 'Date',
        billFile: 'Bill File',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product Name',
        productCode: 'Product Code',
        batchNumber: 'Batch Number',
        price: 'Price',
        quantity: 'Quantity',
        total: 'Total',
        remove: 'Remove',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        grandTotal: 'Grand Total',
        paid: 'Paid Amount',
        balance: 'Balance',
        savePurchase: 'Save Purchase',
        updatePurchase: 'Update Purchase',
        clear: 'Clear',
        saving: 'Saving...',
        purchaseSaved: 'Purchase saved successfully!',
        purchaseUpdated: 'Purchase updated successfully!',
        failedSave: 'Failed to save purchase',
        failedUpdate: 'Failed to update purchase',
        networkError: 'Network error occurred',
        billRequired: 'Bill number is required',
        supplierRequired: 'Supplier is required',
        dateRequired: 'Date is required',
        itemsRequired: 'At least one item is required',
        productRequired: 'Product name is required',
        batchRequired: 'Batch number is required',
        priceRequired: 'Price is required',
        quantityRequired: 'Quantity is required',
        invalidPrice: 'Please enter a valid price',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPaid: 'Please enter a valid paid amount',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        searchSupplier: 'Search supplier...',
        noSuppliersFound: 'No suppliers found',
        category: 'Category',
        company: 'Company',
        packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type',
        itemsPerPack: 'Items/Pack',
        unitOfMeasure: 'Unit of Measure',
        costPerPack: 'Cost Per Pack',
        optional: 'Optional',
        browse: 'Browse',
        generate: 'Generate',
        noFile: 'No file chosen',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        searchProducts: 'Search Products'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const purchasesTranslator = createPurchasesTranslator();

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== IOS STYLE TOGGLE SWITCH ====================
const IOSToggleSwitch = ({ checked, onChange, disabled, label, activeLabel, inactiveLabel }) => {
    return (
        <button
            type="button"
            className={`ozui-ios-toggle ${checked ? 'ozui-checked' : ''}`}
            onClick={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
        >
            <span className="ozui-ios-slider" />
            <span className="ozui-ios-label">
                {checked ? (activeLabel || label || 'Packing') : (inactiveLabel || label || 'Sub Packing')}
            </span>
        </button>
    );
};

// ==================== ITEMS TABLE COMPONENT ====================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProductsByIndex, setFilteredProductsByIndex] = useState({});
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [availableSubPackings, setAvailableSubPackings] = useState({});
    const productSearchRefs = useRef({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_products',
                    username: user?.username,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setProducts(data.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    useEffect(() => {
        loadProducts();
    }, [user?.username, shopId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        items.forEach((item, index) => {
            if (item.packing_type && subPackingTypesMap[item.packing_type]) {
                setAvailableSubPackings(prev => ({
                    ...prev,
                    [index]: subPackingTypesMap[item.packing_type]
                }));
            } else {
                setAvailableSubPackings(prev => ({ ...prev, [index]: [] }));
            }
        });
    }, [items]);

    const handleProductSearch = (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        if (term.trim() === '') {
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        } else {
            const filtered = products.filter(product =>
                product.product_name?.toLowerCase().includes(term.toLowerCase()) ||
                product.batch_no?.toLowerCase().includes(term.toLowerCase()) ||
                product.barcode?.toLowerCase().includes(term.toLowerCase()) ||
                product.product_code?.toLowerCase().includes(term.toLowerCase())
            );
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: filtered }));
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
        }
    };

    const calculateTotal = (item) => {
        const quantity = parseFloat(item.quantity) || 0;

        if (item.purchase_type === 'sub_packing') {
            const pricePerUnit = parseFloat(item.price) || 0;
            return (pricePerUnit * quantity).toFixed(2);
        } else {
            const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
            return (costPerPack * quantity).toFixed(2);
        }
    };

    const selectProduct = (index, product) => {
        if (showToast) {
            showToast('info', `Loading product "${product.product_name}"...`);
        }

        let packagingInfo = null;
        let itemsPerPack = 1;
        let unitOfMeasure = 'piece';
        let costPerPack = '';
        let sellPerPack = '';
        let purchaseType = product.purchase_type || 'packing';
        let packingType = '';
        let subPackingType = '';

        if (product.packaging_info) {
            try {
                packagingInfo = typeof product.packaging_info === 'string' ?
                    JSON.parse(product.packaging_info) : product.packaging_info;
                if (packagingInfo) {
                    itemsPerPack = packagingInfo.items_per_pack || 1;
                    unitOfMeasure = packagingInfo.unit_of_measure || 'piece';
                    costPerPack = packagingInfo.cost_per_pack ? parseFloat(packagingInfo.cost_per_pack).toFixed(2) : '';
                    sellPerPack = packagingInfo.sell_per_pack ? parseFloat(packagingInfo.sell_per_pack).toFixed(2) : '';
                    purchaseType = packagingInfo.purchase_type || purchaseType;
                    packingType = packagingInfo.packing_type || '';
                    subPackingType = packagingInfo.sub_packing_type || '';
                }
            } catch (e) { }
        }

        let calculatedPrice = '';
        if (purchaseType === 'sub_packing') {
            calculatedPrice = product.cost_per_unit || product.cost_price || '';
            if (calculatedPrice && itemsPerPack) {
                const priceNum = parseFloat(calculatedPrice);
                costPerPack = (priceNum * itemsPerPack).toFixed(2);
                calculatedPrice = priceNum.toFixed(2);
            } else if (calculatedPrice) {
                calculatedPrice = parseFloat(calculatedPrice).toFixed(2);
            }
        } else {
            if (costPerPack && itemsPerPack) {
                const perUnit = parseFloat(costPerPack) / parseFloat(itemsPerPack);
                if (!isNaN(perUnit) && isFinite(perUnit)) {
                    calculatedPrice = perUnit.toFixed(2);
                }
            } else if (product.cost_price) {
                calculatedPrice = parseFloat(product.cost_price).toFixed(2);
            } else {
                calculatedPrice = '';
            }
            costPerPack = costPerPack ? parseFloat(costPerPack).toFixed(2) : '';
        }

        const updatedItem = {
            product_name: product.product_name || '',
            batch_no: product.batch_no || '',
            product_code: product.product_code || '',
            category: product.category || '',
            company: product.company || '',
            price: calculatedPrice,
            quantity: '1',
            purchase_type: purchaseType,
            packing_type: packingType,
            sub_packing_type: subPackingType,
            items_per_pack: itemsPerPack,
            unit_of_measure: unitOfMeasure,
            cost_per_pack: costPerPack,
            sell_per_pack: sellPerPack || (product.sell_price ? (parseFloat(product.sell_price) * parseFloat(itemsPerPack)).toFixed(2) : ''),
            product_id: product.id || ''
        };

        onItemChange(index, 'batch_update', updatedItem);
        setProductSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));

        if (showToast) {
            showToast('success', `✓ "${product.product_name}" added (${purchaseType === 'sub_packing' ? 'Sub Packing' : 'Packing'} purchase)`);
        }
    };

    const togglePurchaseType = (index) => {
        const currentItem = items[index];
        const currentType = currentItem.purchase_type || 'packing';
        const newType = currentType === 'packing' ? 'sub_packing' : 'packing';

        if (showToast) {
            showToast('info', newType === 'packing' ? 'Packing Purchase - Updates Stock Quantity' : 'Sub Packing Purchase - Updates Bulk Quantity');
        }

        onItemChange(index, 'purchase_type', newType);
    };

    const handlePackingTypeChange = (index, packingType) => {
        onItemChange(index, 'packing_type', packingType);
        onItemChange(index, 'sub_packing_type', '');
        onItemChange(index, 'items_per_pack', '');
        onItemChange(index, 'unit_of_measure', '');
        onItemChange(index, 'cost_per_pack', '');
    };

    const handleSubPackingChange = (index, subPackingType) => {
        onItemChange(index, 'sub_packing_type', subPackingType);
        const subPackingOption = availableSubPackings[index]?.find(sp => sp.value === subPackingType);
        if (subPackingOption && subPackingOption.defaultQty) {
            onItemChange(index, 'items_per_pack', subPackingOption.defaultQty);
        }
    };

    const handleItemsPerPackChange = (index, itemsPerPackValue) => {
        const itemsPerPack = parseFloat(itemsPerPackValue) || 1;
        onItemChange(index, 'items_per_pack', itemsPerPack);
        const item = items[index];

        if (item.cost_per_pack && itemsPerPack) {
            const perUnitCost = parseFloat(item.cost_per_pack) / itemsPerPack;
            if (!isNaN(perUnitCost) && isFinite(perUnitCost)) {
                onItemChange(index, 'price', perUnitCost.toFixed(2));
            }
        }
    };

    const handlePackPriceChange = (index, field, value) => {
        const roundedValue = value ? parseFloat(value).toFixed(2) : '';
        onItemChange(index, field, roundedValue);
        const item = items[index];
        const itemsPerPack = parseFloat(item.items_per_pack) || 1;

        if (field === 'cost_per_pack' && value && itemsPerPack) {
            const perUnitPrice = parseFloat(value) / itemsPerPack;
            if (!isNaN(perUnitPrice) && isFinite(perUnitPrice)) {
                onItemChange(index, 'price', perUnitPrice.toFixed(2));
            }
        }
    };

    const isSubPackingType = (item) => {
        return item.purchase_type === 'sub_packing';
    };

    // DESKTOP VIEW
    if (!isMobile) {
        return (
            <div className="ozui-items-table-container">
                <div className="ozui-table-header-actions">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations?.items || 'Items'}</h4>
                    <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="ozui-table-wrapper">
                    <table className="ozui-items-table">
                        <thead>
                            <tr>
                                <th>{translations?.productName || 'Product Name'} <span className="ozui-required">*</span></th>
                                <th>{translations?.batchNumber || 'Batch No'} <span className="ozui-required">*</span></th>
                                <th>{translations?.productCode || 'Product Code'}</th>
                                <th>{translations?.purchaseType || 'Type'}</th>
                                <th>{translations?.packingType || 'Packing'}</th>
                                <th>{translations?.subPackingType || 'Sub Packing'}</th>
                                <th>{translations?.itemsPerPack || 'Items/Pack'}</th>
                                <th>{translations?.unitOfMeasure || 'UOM'}</th>
                                <th>{translations?.costPerPack || 'Cost/Pack'}</th>
                                <th>{translations?.company || 'Company'}</th>
                                <th>{translations?.price || 'Price/Unit'} <span className="ozui-required">*</span></th>
                                <th>{translations?.quantity || 'Quantity'} <span className="ozui-required">*</span></th>
                                <th>{translations?.total || 'Total'}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const filteredProductsForIndex = filteredProductsByIndex[index] || [];
                                const showDropdown = showProductDropdown[index] || false;
                                const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;
                                const isSubPacking = isSubPackingType(item);
                                const itemTotal = calculateTotal(item);

                                return (
                                    <tr key={index} className={`${errors[`product_name_${index}`] ? 'ozui-error-row' : ''}`}>
                                        <td>
                                            <div className="ozui-table-field" ref={el => productSearchRefs.current[index] = el}>
                                                <div className="ozui-product-search-container">
                                                    <input
                                                        type="text"
                                                        value={searchTerm}
                                                        onChange={(e) => handleProductSearch(index, e.target.value)}
                                                        onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                                        placeholder={translations?.searchProduct || 'Search product...'}
                                                        disabled={disabled}
                                                        autoComplete="off"
                                                        className={errors[`product_name_${index}`] ? 'ozui-error' : ''}
                                                    />
                                                    {showDropdown && filteredProductsForIndex.length > 0 && (
                                                        <div className="ozui-product-dropdown-table">
                                                            {filteredProductsForIndex.map(product => (
                                                                <div key={product.id} className="ozui-product-option" onClick={() => selectProduct(index, product)}>
                                                                    <div className="ozui-product-name">{product.product_name}</div>
                                                                    <div className="ozui-product-details">
                                                                        {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                                        {product.product_code && <span><FontAwesomeIcon icon={faBarcode} /> Code: {product.product_code}</span>}
                                                                    </div>
                                                                    <div className="ozui-product-item-price-stock">
                                                                        {product.sell_price && (
                                                                            <span className="ozui-product-price">
                                                                                <FontAwesomeIcon icon={faRupeeSign} /> {parseFloat(product.sell_price).toFixed(2)}
                                                                            </span>
                                                                        )}
                                                                        <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                                            Stock: {product.quantity} units
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {errors[`product_name_${index}`] && <div className="ozui-table-error">{errors[`product_name_${index}`]}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={item.batch_no || ''}
                                                    onChange={(e) => onItemChange(index, 'batch_no', e.target.value)}
                                                    placeholder="Batch no"
                                                    disabled={disabled}
                                                    className={errors[`batch_no_${index}`] ? 'ozui-error' : ''}
                                                    style={{ backgroundColor: item.batch_no ? '#e8f5e9' : '' }}
                                                />
                                                {errors[`batch_no_${index}`] && <div className="ozui-table-error">{errors[`batch_no_${index}`]}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={item.product_code || ''}
                                                    onChange={(e) => onItemChange(index, 'product_code', e.target.value)}
                                                    placeholder="Product code"
                                                    disabled={disabled}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <IOSToggleSwitch
                                                    checked={!isSubPacking}
                                                    onChange={() => togglePurchaseType(index)}
                                                    disabled={disabled}
                                                    activeLabel="Packing"
                                                    inactiveLabel="Sub Packing"
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                {!isSubPacking ? (
                                                    <select
                                                        value={item.packing_type || ''}
                                                        onChange={(e) => handlePackingTypeChange(index, e.target.value)}
                                                        disabled={disabled}
                                                    >
                                                        {packingTypesList.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                                    </select>
                                                ) : (
                                                    <div className="ozui-disabled-placeholder">—</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                {!isSubPacking ? (
                                                    <select
                                                        value={item.sub_packing_type || ''}
                                                        onChange={(e) => handleSubPackingChange(index, e.target.value)}
                                                        disabled={disabled || !item.packing_type}
                                                    >
                                                        <option value="">Select</option>
                                                        {(availableSubPackings[index] || []).map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}
                                                    </select>
                                                ) : (
                                                    <div className="ozui-disabled-placeholder">—</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                {!isSubPacking ? (
                                                    <input
                                                        type="number"
                                                        value={item.items_per_pack || ''}
                                                        onChange={(e) => handleItemsPerPackChange(index, e.target.value)}
                                                        step="0.1"
                                                        min="0"
                                                        disabled={disabled}
                                                    />
                                                ) : (
                                                    <div className="ozui-disabled-placeholder">—</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                {!isSubPacking ? (
                                                    <select
                                                        value={item.unit_of_measure || ''}
                                                        onChange={(e) => onItemChange(index, 'unit_of_measure', e.target.value)}
                                                        disabled={disabled}
                                                    >
                                                        {unitsOfMeasure.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                                                    </select>
                                                ) : (
                                                    <div className="ozui-disabled-placeholder">—</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                {!isSubPacking ? (
                                                    <input
                                                        type="number"
                                                        value={item.cost_per_pack || ''}
                                                        onChange={(e) => handlePackPriceChange(index, 'cost_per_pack', e.target.value)}
                                                        step="0.1"
                                                        min="0"
                                                        disabled={disabled}
                                                        placeholder="0"
                                                    />
                                                ) : (
                                                    <div className="ozui-disabled-placeholder">—</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={item.company || ''}
                                                    onChange={(e) => onItemChange(index, 'company', e.target.value)}
                                                    disabled={disabled}
                                                    placeholder="Company"
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="number"
                                                    value={item.price}
                                                    onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                                    step="0.1"
                                                    min="0"
                                                    disabled={disabled}
                                                    className={errors[`price_${index}`] ? 'ozui-error' : ''}
                                                    placeholder="0"
                                                />
                                                {errors[`price_${index}`] && <div className="ozui-table-error">{errors[`price_${index}`]}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                                    step="0.1"
                                                    min="0.1"
                                                    disabled={disabled}
                                                    className={errors[`quantity_${index}`] ? 'ozui-error' : ''}
                                                    placeholder="0"
                                                />
                                                {errors[`quantity_${index}`] && <div className="ozui-table-error">{errors[`quantity_${index}`]}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={itemTotal}
                                                    readOnly
                                                    disabled
                                                    className="ozui-readonly-input ozui-total-display"
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="ozui-table-remove-btn"
                                                onClick={() => onRemove(index)}
                                                disabled={disabled || items.length === 1}
                                                title={translations?.remove || 'Remove'}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // MOBILE VIEW
    return (
        <div className="ozui-items-mobile-container">
            <div className="ozui-items-header-mobile">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.items || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-items-cards-container">
                {items.map((item, index) => {
                    const filteredProductsForIndex = filteredProductsByIndex[index] || [];
                    const showDropdown = showProductDropdown[index] || false;
                    const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;
                    const isSubPacking = isSubPackingType(item);
                    const itemTotal = calculateTotal(item);

                    return (
                        <div key={index} className={`ozui-item-card-mobile ${isSubPacking ? 'ozui-subpacking-card' : 'ozui-packing-card'}`}>
                            <div className="ozui-item-card-header">
                                <span className="ozui-item-number">Item {index + 1}</span>
                                <IOSToggleSwitch
                                    checked={!isSubPacking}
                                    onChange={() => togglePurchaseType(index)}
                                    disabled={disabled}
                                    activeLabel="Packing"
                                    inactiveLabel="Sub Packing"
                                />
                                <button type="button" className="ozui-item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                            <div className="ozui-item-card-content">
                                <div className={`ozui-mobile-field ${errors[`product_name_${index}`] ? 'ozui-has-error' : ''}`} ref={el => productSearchRefs.current[index] = el}>
                                    <label>{translations?.productName || 'Product Name'} <span className="required">*</span></label>
                                    <div className="ozui-product-search-container">
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => handleProductSearch(index, e.target.value)}
                                            onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                            placeholder={translations?.searchProduct || 'Search product...'}
                                            disabled={disabled}
                                            autoComplete="off"
                                        />
                                        {showDropdown && filteredProductsForIndex.length > 0 && (
                                            <div className="ozui-product-dropdown-mobile">
                                                {filteredProductsForIndex.map(product => (
                                                    <div key={product.id} className="ozui-product-option" onClick={() => selectProduct(index, product)}>
                                                        <div className="ozui-product-name">{product.product_name}</div>
                                                        <div className="ozui-product-details">
                                                            {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {errors[`product_name_${index}`] && <div className="ozui-error-text">{errors[`product_name_${index}`]}</div>}
                                </div>

                                <div className={`ozui-mobile-field ${errors[`batch_no_${index}`] ? 'ozui-has-error' : ''}`}>
                                    <label>{translations?.batchNumber || 'Batch Number'} <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={item.batch_no || ''}
                                        onChange={(e) => onItemChange(index, 'batch_no', e.target.value)}
                                        placeholder="Batch number"
                                        disabled={disabled}
                                    />
                                    {errors[`batch_no_${index}`] && <div className="ozui-error-text">{errors[`batch_no_${index}`]}</div>}
                                </div>

                                {!isSubPacking && (
                                    <>
                                        <div className="ozui-mobile-field">
                                            <label>{translations?.packingType || 'Packing Type'}</label>
                                            <select
                                                value={item.packing_type || ''}
                                                onChange={(e) => handlePackingTypeChange(index, e.target.value)}
                                                disabled={disabled}
                                            >
                                                {packingTypesList.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                            </select>
                                        </div>

                                        <div className="ozui-mobile-field">
                                            <label>{translations?.subPackingType || 'Sub Packing Type'}</label>
                                            <select
                                                value={item.sub_packing_type || ''}
                                                onChange={(e) => handleSubPackingChange(index, e.target.value)}
                                                disabled={disabled || !item.packing_type}
                                            >
                                                <option value="">Select</option>
                                                {(availableSubPackings[index] || []).map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}
                                            </select>
                                        </div>

                                        <div className="ozui-mobile-field">
                                            <label>{translations?.itemsPerPack || 'Items Per Pack'}</label>
                                            <input
                                                type="number"
                                                value={item.items_per_pack || ''}
                                                onChange={(e) => handleItemsPerPackChange(index, e.target.value)}
                                                step="0.001"
                                                min="0"
                                                disabled={disabled}
                                            />
                                        </div>

                                        <div className="ozui-mobile-field">
                                            <label>{translations?.unitOfMeasure || 'Unit of Measure'}</label>
                                            <select
                                                value={item.unit_of_measure || ''}
                                                onChange={(e) => onItemChange(index, 'unit_of_measure', e.target.value)}
                                                disabled={disabled}
                                            >
                                                {unitsOfMeasure.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                                            </select>
                                        </div>

                                        <div className="ozui-mobile-field">
                                            <label>{translations?.costPerPack || 'Cost Per Pack'}</label>
                                            <input
                                                type="number"
                                                value={item.cost_per_pack || ''}
                                                onChange={(e) => handlePackPriceChange(index, 'cost_per_pack', e.target.value)}
                                                step="0.01"
                                                min="0"
                                                disabled={disabled}
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </>
                                )}

                                {isSubPacking && (
                                    <div className="ozui-info-message">
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                        <span>This purchase will only update Bulk Quantity (BQuantity)</span>
                                    </div>
                                )}

                                <div className="ozui-mobile-field">
                                    <label>{translations?.company || 'Company'}</label>
                                    <input
                                        type="text"
                                        value={item.company || ''}
                                        onChange={(e) => onItemChange(index, 'company', e.target.value)}
                                        disabled={disabled}
                                    />
                                </div>

                                <div className="ozui-mobile-row">
                                    <div className={`ozui-mobile-field half ${errors[`price_${index}`] ? 'ozui-has-error' : ''}`}>
                                        <label>{translations?.price || 'Price/Unit'} <span className="required">*</span></label>
                                        <input
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                            step="0.01"
                                            min="0"
                                            disabled={disabled}
                                        />
                                        {errors[`price_${index}`] && <div className="ozui-error-text">{errors[`price_${index}`]}</div>}
                                    </div>
                                    <div className={`ozui-mobile-field half ${errors[`quantity_${index}`] ? 'ozui-has-error' : ''}`}>
                                        <label>{translations?.quantity || 'Quantity'} {!isSubPacking ? '(Packs)' : '(Items)'} <span className="required">*</span></label>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            disabled={disabled}
                                        />
                                        {errors[`quantity_${index}`] && <div className="ozui-error-text">{errors[`quantity_${index}`]}</div>}
                                    </div>
                                </div>

                                <div className="ozui-mobile-field">
                                    <label>{translations?.total || 'Total'}</label>
                                    <input
                                        type="text"
                                        value={itemTotal}
                                        readOnly
                                        disabled
                                        className="ozui-readonly-input ozui-total-display"
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==================== MAIN PURCHASES COMPONENT ====================
const Purchases = ({ purchaseId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        bill_file: null,
        grand_total: '0.00',
        paid: '0.00',
        balance: '0.00',
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [hasDraft, setHasDraft] = useState(false);

    // Draft State
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    // Global Product Search State
    const [globalProductSearchTerm, setGlobalProductSearchTerm] = useState('');
    const [filteredGlobalProducts, setFilteredGlobalProducts] = useState([]);
    const [showGlobalProductDropdown, setShowGlobalProductDropdown] = useState(false);
    const [searchingGlobalProducts, setSearchingGlobalProducts] = useState(false);
    const productSearchRef = useRef(null);

    // Auto-focus ref for Bill Number field (first text input after date)
    const billNumberRef = useRef(null);

    // Refs
    const toastTimerRef = useRef(null);
    const supplierSearchRef = useRef(null);
    const fileInputRef = useRef(null);
    const isSavingRef = useRef(false);
    const isClearingRef = useRef(false);
    const isInitialLoadRef = useRef(true);
    const draftsDropdownRef = useRef(null);

    // Tab key handler to close all dropdowns
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowSupplierDropdown(false);
                setShowGlobalProductDropdown(false);
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus on Bill Number field when component mounts
    useEffect(() => {
        setTimeout(() => {
            if (billNumberRef.current) {
                billNumberRef.current.focus();
            }
        }, 100);
    }, []);

    // ============================================
    // Draft Management Functions
    // ============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `purchase_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.bill_no?.trim() || formData.supplierid || (formData.items && formData.items.some(item => item.product_name?.trim()));
        if (!hasData) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }

        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };

        const existingDrafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(limitedDrafts));

        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            ...initialFormData,
            ...draft,
            date: draft.date || new Date().toISOString().split('T')[0],
            items: draft.items || []
        });
        if (draft.supplier_name) setSearchTerm(draft.supplier_name);
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');

        setTimeout(() => {
            if (billNumberRef.current) {
                billNumberRef.current.focus();
            }
        }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearDraft = () => {
        if (isSavingRef.current) { showToast('warning', 'Please wait for current operation to complete'); return; }
        isClearingRef.current = true;
        clearDraftFromStorage();
        setFormData({
            ...initialFormData,
            items: []
        });
        setSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        setHasDraft(false);
        setGlobalProductSearchTerm('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        showToast('success', translations.draftCleared || 'Form cleared');

        setTimeout(() => {
            if (billNumberRef.current) {
                billNumberRef.current.focus();
            }
        }, 100);

        setTimeout(() => { isClearingRef.current = false; }, 500);
    };

    const clearDraftFromStorage = () => {
        try {
            localStorage.removeItem(getDraftStorageKey());
            setHasDraft(false);
        } catch (error) { }
    };

    const checkDraftExists = () => {
        try {
            setHasDraft(!!localStorage.getItem(getDraftStorageKey()));
        } catch (error) { }
    };

    // ============================================
    // Theme Color Auto-Sync
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // ============================================
    // Dark Mode Auto-Sync
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }

            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ============================================
    // Language Auto-Sync
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await purchasesTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL Direction Sync
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load drafts on shop change
    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Position drafts dropdown
    useEffect(() => {
        if (showDraftsList && draftsDropdownRef.current) {
            const button = draftsDropdownRef.current.querySelector('.drafts-btn');
            const dropdown = draftsDropdownRef.current.querySelector('.ozui-drafts-dropdown');

            if (button && dropdown) {
                const rect = button.getBoundingClientRect();
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${rect.bottom + 8}px`;
                dropdown.style.right = `${window.innerWidth - rect.right}px`;
            }
        }
    }, [showDraftsList]);

    // Click outside for drafts dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Click outside for supplier dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (supplierSearchRef.current && !supplierSearchRef.current.contains(event.target)) {
                setShowSupplierDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Click outside for global product dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
                setShowGlobalProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    useEffect(() => { checkDraftExists(); }, [currentShop?.shop_id]);

    useEffect(() => {
        if (!isEditMode && !purchaseId && currentShop?.shop_id && isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            const savedDraft = localStorage.getItem(getDraftStorageKey());
            if (savedDraft) {
                try {
                    const draftData = JSON.parse(savedDraft);
                    setFormData(prev => ({ ...prev, ...draftData.formData }));
                    if (draftData.searchTerm) setSearchTerm(draftData.searchTerm);
                    setHasDraft(true);
                    showToast('info', translations?.draftLoaded || 'Loaded saved draft');
                } catch (error) { }
            }
        }
    }, [currentShop?.shop_id, isEditMode, purchaseId, translations]);

    const loadSuppliers = async () => {
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: '', username: user?.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setSuppliers(data.data);
                setFilteredSuppliers(data.data);
            }
        } catch (error) { console.error('Error loading suppliers:', error); }
    };

    useEffect(() => { loadSuppliers(); }, [user?.username, currentShop?.shop_id]);

    useEffect(() => { if (purchaseId) { setIsEditMode(true); loadPurchaseData(purchaseId); } }, [purchaseId]);

    useEffect(() => { calculateTotals(); }, [formData.items, formData.paid]);

    const loadPurchaseData = async (id) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user?.username, shop_id: currentShop?.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const purchase = data.data;
                setFormData({
                    date: purchase.date || new Date().toISOString().split('T')[0],
                    bill_no: purchase.bill_no || '',
                    supplierid: purchase.supplierid || '',
                    supplier_name: purchase.supplier_name || '',
                    phone: purchase.phone || '',
                    address: purchase.address || '',
                    bill_file: null,
                    grand_total: purchase.grand_total || '0.00',
                    paid: purchase.paid || '0.00',
                    balance: purchase.balance || '0.00',
                    items: purchase.items || []
                });
                if (purchase.supplier_name) setSearchTerm(purchase.supplier_name);
            } else { showToast('error', data.message || 'Failed to load purchase data'); }
        } catch (error) { showToast('error', 'Network error occurred'); }
        finally { setLoading(false); }
    };

    const searchGlobalProducts = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredGlobalProducts([]);
            setShowGlobalProductDropdown(false);
            return;
        }
        setSearchingGlobalProducts(true);
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_products',
                    searchTerm: searchTerm,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const products = (data.data || []).map(product => {
                    let hasPackaging = false;
                    let packingType = null;
                    let subPackingType = null;
                    let itemsPerPack = 1;
                    let unitOfMeasure = product.unit_of_measure || 'piece';
                    let sellPerPack = null;

                    if (product.packaging_info) {
                        try {
                            const info = typeof product.packaging_info === 'string' ? JSON.parse(product.packaging_info) : product.packaging_info;
                            if (info) {
                                hasPackaging = true;
                                packingType = info.packing_type;
                                subPackingType = info.sub_packing_type;
                                itemsPerPack = parseFloat(info.items_per_pack) || 1;
                                unitOfMeasure = info.unit_of_measure || product.unit_of_measure || 'piece';
                                sellPerPack = info.sell_per_pack ? parseFloat(info.sell_per_pack) : null;
                            }
                        } catch (e) { }
                    }

                    return {
                        id: product.id,
                        product_name: product.product_name,
                        product_code: product.product_code || '',
                        batch_no: product.batch_no || '',
                        barcode: product.barcode || '',
                        sell_price: parseFloat(product.sell_price) || 0,
                        sell_per_pack: sellPerPack,
                        quantity: parseFloat(product.quantity) || 0,
                        quantity_alert: parseFloat(product.quantity_alert) || 5,
                        category: product.category || '',
                        company: product.company || '',
                        has_packaging: hasPackaging,
                        packing_type: packingType,
                        sub_packing_type: subPackingType,
                        items_per_pack: itemsPerPack,
                        unit_of_measure: unitOfMeasure,
                        cost_price: parseFloat(product.cost_price) || 0,
                        packaging_info: product.packaging_info
                    };
                });
                setFilteredGlobalProducts(products);
                setShowGlobalProductDropdown(products.length > 0);
            } else {
                setFilteredGlobalProducts([]);
                setShowGlobalProductDropdown(false);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setFilteredGlobalProducts([]);
            setShowGlobalProductDropdown(false);
        } finally {
            setSearchingGlobalProducts(false);
        }
    }, [user?.username, currentShop?.shop_id]);

    const addProductToItems = (product) => {
        const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);

        if (existingItemIndex >= 0) {
            const updatedItems = [...formData.items];
            const existingItem = updatedItems[existingItemIndex];
            const newQty = (parseFloat(existingItem.quantity) || 0) + 1;
            updatedItems[existingItemIndex] = {
                ...existingItem,
                quantity: newQty.toString()
            };
            setFormData(prev => ({ ...prev, items: updatedItems }));
            showToast('success', `Added another ${product.product_name}`);
        } else {
            let packagingInfo = null;
            let itemsPerPack = 1;
            let unitOfMeasure = 'piece';
            let costPerPack = '';
            let sellPerPack = '';
            let purchaseType = 'packing';
            let packingType = '';
            let subPackingType = '';

            if (product.packaging_info) {
                try {
                    packagingInfo = typeof product.packaging_info === 'string' ?
                        JSON.parse(product.packaging_info) : product.packaging_info;
                    if (packagingInfo) {
                        itemsPerPack = packagingInfo.items_per_pack || 1;
                        unitOfMeasure = packagingInfo.unit_of_measure || 'piece';
                        costPerPack = packagingInfo.cost_per_pack ? parseFloat(packagingInfo.cost_per_pack).toFixed(2) : '';
                        sellPerPack = packagingInfo.sell_per_pack ? parseFloat(packagingInfo.sell_per_pack).toFixed(2) : '';
                        purchaseType = packagingInfo.purchase_type || purchaseType;
                        packingType = packagingInfo.packing_type || '';
                        subPackingType = packagingInfo.sub_packing_type || '';
                    }
                } catch (e) { }
            }

            let calculatedPrice = '';
            if (purchaseType === 'sub_packing') {
                calculatedPrice = product.cost_price || '';
                if (calculatedPrice && itemsPerPack) {
                    const priceNum = parseFloat(calculatedPrice);
                    costPerPack = (priceNum * itemsPerPack).toFixed(2);
                    calculatedPrice = priceNum.toFixed(2);
                } else if (calculatedPrice) {
                    calculatedPrice = parseFloat(calculatedPrice).toFixed(2);
                }
            } else {
                if (costPerPack && itemsPerPack) {
                    const perUnit = parseFloat(costPerPack) / parseFloat(itemsPerPack);
                    if (!isNaN(perUnit) && isFinite(perUnit)) {
                        calculatedPrice = perUnit.toFixed(2);
                    }
                } else if (product.cost_price) {
                    calculatedPrice = parseFloat(product.cost_price).toFixed(2);
                } else {
                    calculatedPrice = '';
                }
                costPerPack = costPerPack ? parseFloat(costPerPack).toFixed(2) : '';
            }

            const newItem = {
                product_name: product.product_name || '',
                batch_no: product.batch_no || '',
                product_code: product.product_code || '',
                category: product.category || '',
                company: product.company || '',
                price: calculatedPrice,
                quantity: '1',
                purchase_type: purchaseType,
                packing_type: packingType,
                sub_packing_type: subPackingType,
                items_per_pack: itemsPerPack,
                unit_of_measure: unitOfMeasure,
                cost_per_pack: costPerPack,
                sell_per_pack: sellPerPack || (product.sell_price ? (parseFloat(product.sell_price) * parseFloat(itemsPerPack)).toFixed(2) : ''),
                product_id: product.id || ''
            };

            setFormData(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));
            showToast('success', `${product.product_name} added (${purchaseType === 'sub_packing' ? 'Sub Packing' : 'Packing'} purchase)`);
        }

        setGlobalProductSearchTerm('');
        setShowGlobalProductDropdown(false);
    };

    const handleSupplierSearch = async (term) => {
        setSearchTerm(term);
        setShowSupplierDropdown(true);
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: term, username: user?.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) setFilteredSuppliers(data.data);
        } catch (error) { console.error('Error searching suppliers:', error); }
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({ ...prev, supplierid: supplier.supplierid, supplier_name: supplier.supplier_name || '', phone: supplier.phone || '', address: supplier.address || '' }));
        setSearchTerm(supplier.supplier_name || '');
        setShowSupplierDropdown(false);
        if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
        showToast('success', `Supplier "${supplier.supplier_name}" selected`);
    };

    const handleItemChange = (index, field, value) => {
        if (field === 'batch_update') {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems[index] = { ...updatedItems[index], ...value };
                return { ...prev, items: updatedItems };
            });
        } else {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems[index] = { ...updatedItems[index], [field]: value };
                return { ...prev, items: updatedItems };
            });
        }
        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) setErrors(prev => { const newErrors = { ...prev }; delete newErrors[errorKey]; return newErrors; });
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_name: '', batch_no: '', product_code: '', product_id: '', category: '', company: '', price: '', quantity: '',
                purchase_type: 'packing', packing_type: '', sub_packing_type: '', items_per_pack: '', unit_of_measure: '', cost_per_pack: '', sell_per_pack: ''
            }]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems.splice(index, 1);
                return { ...prev, items: updatedItems };
            });
        } else if (formData.items.length === 1) {
            setFormData(prev => ({
                ...prev,
                items: []
            }));
        }
    };

    const calculateTotals = () => {
        let grandTotal = 0;
        formData.items.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            if (item.purchase_type === 'sub_packing') {
                const pricePerUnit = parseFloat(item.price) || 0;
                grandTotal += pricePerUnit * quantity;
            } else {
                const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
                grandTotal += costPerPack * quantity;
            }
        });
        const paid = parseFloat(formData.paid) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({ ...prev, grand_total: grandTotal.toFixed(2), balance: balance.toFixed(2) }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { showToast('error', 'File size must be less than 5MB'); return; }
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData(prev => ({ ...prev, bill_file: { name: file.name, type: file.type, data: event.target.result.split(',')[1] } }));
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.bill_no?.trim()) newErrors.bill_no = translations?.billRequired || 'Bill number is required';
        if (!formData.supplierid) newErrors.supplier = translations?.supplierRequired || 'Supplier is required';
        if (!formData.date) newErrors.date = translations?.dateRequired || 'Date is required';
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                if (!item.product_name?.trim()) newErrors[`product_name_${index}`] = translations?.productRequired || 'Product name is required';
                if (!item.batch_no?.trim()) newErrors[`batch_no_${index}`] = translations?.batchRequired || 'Batch number is required';

                if (item.purchase_type === 'packing') {
                    if (!item.cost_per_pack || parseFloat(item.cost_per_pack) <= 0) {
                        newErrors[`cost_per_pack_${index}`] = 'Cost per pack is required';
                    }
                }

                if (!item.price || parseFloat(item.price) <= 0) {
                    newErrors[`price_${index}`] = translations?.priceRequired || 'Price is required';
                }

                if (!item.quantity || parseFloat(item.quantity) <= 0) {
                    newErrors[`quantity_${index}`] = translations?.quantityRequired || 'Quantity is required';
                }
            });
        }

        const paid = parseFloat(formData.paid);
        if (isNaN(paid) || paid < 0) newErrors.paid = translations?.invalidPaid || 'Please enter a valid paid amount';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        if (name === 'paid') {
            const paidVal = parseFloat(value) || 0;
            const grandTotalVal = parseFloat(formData.grand_total) || 0;
            setFormData(prev => ({ ...prev, balance: (grandTotalVal - paidVal).toFixed(2) }));
        }
    };

    const generateNextBillNumber = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `PUR-${year}${month}${day}-${random}`;
    };

    const handleGenerateBillNumber = () => {
        if (!formData.bill_no) setFormData(prev => ({ ...prev, bill_no: generateNextBillNumber() }));
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("Submit clicked!");

    // Prevent multiple submissions
    if (isSavingRef.current) {
        showToast('warning', 'Please wait...');
        return;
    }

    // Check if shop is selected
    if (!currentShop?.shop_id) {
        showToast('error', 'No shop selected. Please select a shop first.');
        return;
    }

    // Check for at least one item
    if (!formData.items || formData.items.length === 0) {
        showToast('error', 'Please add at least one item');
        return;
    }

    // Check required fields
    if (!formData.bill_no?.trim()) {
        showToast('error', 'Bill number is required');
        return;
    }

    if (!formData.supplierid) {
        showToast('error', 'Supplier is required');
        return;
    }

    // Check each item
    for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        if (!item.product_name?.trim()) {
            showToast('error', `Product name is required for item ${i + 1}`);
            return;
        }
        if (!item.batch_no?.trim()) {
            showToast('error', `Batch number is required for item ${i + 1}`);
            return;
        }
        if (!item.price || parseFloat(item.price) <= 0) {
            showToast('error', `Valid price is required for item ${i + 1}`);
            return;
        }
        if (!item.quantity || parseFloat(item.quantity) <= 0) {
            showToast('error', `Valid quantity is required for item ${i + 1}`);
            return;
        }
    }

    // Set saving state
    isSavingRef.current = true;
    setSaving(true);

    try {
        const currentUsername = user?.username || '';
        if (!currentUsername) {
            throw new Error("User not authenticated");
        }

        // Process items for submission
        const processedItems = formData.items.map(item => {
            const isSubPacking = item.purchase_type === 'sub_packing';
            const itemsPerPack = parseFloat(item.items_per_pack) || 1;
            const quantityInPacks = parseFloat(item.quantity) || 0;

            if (isSubPacking) {
                const pricePerUnit = parseFloat(item.price) || 0;
                const totalPrice = pricePerUnit * quantityInPacks;

                return {
                    product_name: item.product_name || '',
                    batch_no: item.batch_no || '',
                    product_code: item.product_code || '',
                    category: item.category || '',
                    company: item.company || '',
                    price: pricePerUnit,
                    quantity: quantityInPacks,
                    total: totalPrice.toFixed(2),
                    purchase_type: 'sub_packing',
                    packing_type: null,
                    sub_packing_type: null,
                    items_per_pack: itemsPerPack,
                    unit_of_measure: item.unit_of_measure || 'piece',
                    cost_per_pack: (pricePerUnit * itemsPerPack).toFixed(2),
                    is_sub_packing_purchase: true,
                    product_id: item.product_id || null
                };
            } else {
                const costPerPack = parseFloat(item.cost_per_pack) || (parseFloat(item.price) * itemsPerPack);
                const totalPrice = costPerPack * quantityInPacks;
                const pricePerUnit = costPerPack / itemsPerPack;

                return {
                    product_name: item.product_name || '',
                    batch_no: item.batch_no || '',
                    product_code: item.product_code || '',
                    category: item.category || '',
                    company: item.company || '',
                    price: pricePerUnit,
                    quantity: quantityInPacks,
                    total: totalPrice.toFixed(2),
                    purchase_type: 'packing',
                    packing_type: item.packing_type || null,
                    sub_packing_type: item.sub_packing_type || null,
                    items_per_pack: itemsPerPack,
                    unit_of_measure: item.unit_of_measure || 'piece',
                    cost_per_pack: costPerPack,
                    is_sub_packing_purchase: false,
                    product_id: item.product_id || null
                };
            }
        });

        // Prepare payload
        const payload = {
            action: isEditMode ? 'edit' : 'add',
            username: currentUsername,
            shop_id: parseInt(currentShop.shop_id),
            date: formData.date,
            bill_no: formData.bill_no,
            supplierid: parseInt(formData.supplierid) || 0,
            supplier_name: formData.supplier_name,
            phone: formData.phone || '',
            address: formData.address || '',
            grand_total: parseFloat(formData.grand_total || 0).toFixed(2),
            paid: parseFloat(formData.paid || 0).toFixed(2),
            items: processedItems
        };

        // Add bill file if exists
        if (formData.bill_file) {
            payload.bill_file = JSON.stringify(formData.bill_file);
        }

        // Add ID if editing
        if (isEditMode && purchaseId) {
            payload.id = purchaseId;
        }

        // Send request
        const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.status === 'success') {
            showToast('success', isEditMode ? 'Purchase updated successfully!' : 'Purchase saved successfully!');
            clearDraftFromStorage();

            if (!isEditMode) {
                setFormData({
                    ...initialFormData,
                    date: new Date().toISOString().split('T')[0],
                    bill_no: '',
                    paid: '0.00',
                    balance: '0.00',
                    items: []
                });
                setSearchTerm('');
                setErrors({});
                setGlobalProductSearchTerm('');

                setTimeout(() => {
                    if (billNumberRef.current) {
                        billNumberRef.current.focus();
                    }
                }, 100);
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            if (onSuccess) {
                onSuccess(data.purchaseid || purchaseId);
            }

        } else {
            showToast('error', data.message || 'Failed to save purchase');
        }

    } catch (error) {
        console.error('Purchase submission error:', error);
        showToast('error', error.message || 'Network error occurred');

    } finally {
        isSavingRef.current = false;
        setSaving(false);
    }
};

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations?.editPurchase || 'Edit Purchase') : (translations?.addPurchase || 'New Purchase')}</h1>
                            <p>{translations?.purchaseDetails || 'Purchase Details'}</p>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={clearDraft}
                                disabled={saving}
                                title={translations?.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>

                            <button
                                className="ozui-action-btn save-draft"
                                onClick={saveDraft}
                                disabled={saving}
                                title={translations?.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>

                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations?.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>

                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.bill_no || `Purchase #${draft.id}`}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.supplier_name && <span>{draft.supplier_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations?.date || 'Date'} <span>*</span>
                            </label>
                        </div>
                        {errors.date && <small>{errors.date}</small>}
                    </div>

                    <div className={`ozui-field ${errors.bill_no ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper ozui-bill-input-wrapper">
                            <input
                                ref={billNumberRef}
                                type="text"
                                name="bill_no"
                                id="bill_no"
                                value={formData.bill_no}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="bill_no">
                                <FontAwesomeIcon icon={faFileInvoice} />
                                {translations?.billNumber || 'Bill Number'} <span>*</span>
                            </label>
                            {!formData.bill_no && !saving && (
                                <button
                                    type="button"
                                    className="ozui-input-generate-btn"
                                    onClick={handleGenerateBillNumber}
                                    title={translations?.generate || 'Generate'}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            )}
                        </div>
                        {errors.bill_no && <small>{errors.bill_no}</small>}
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.supplier ? 'ozui-error' : ''}`} ref={supplierSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="supplier_search"
                                value={searchTerm}
                                onChange={(e) => handleSupplierSearch(e.target.value)}
                                onFocus={() => setShowSupplierDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="supplier_search">
                                <FontAwesomeIcon icon={faUser} />
                                {translations?.supplier || 'Supplier'} <span>*</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showSupplierDropdown && (
                            <div className="ozui-supplier-dropdown">
                                {filteredSuppliers && filteredSuppliers.length > 0 ? (
                                    filteredSuppliers.map(supplier => (
                                        <div key={supplier.supplierid} className="ozui-dropdown-item" onClick={() => selectSupplier(supplier)}>
                                            <div className="ozui-supplier-name">{supplier.supplier_name}</div>
                                            {supplier.phone && <div className="ozui-supplier-phone"><FontAwesomeIcon icon={faPhone} /> {supplier.phone}</div>}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-dropdown-empty">{translations?.noSuppliersFound || 'No suppliers found'}</div>
                                )}
                            </div>
                        )}
                        {errors.supplier && <small>{errors.supplier}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="supplier_name"
                                id="supplier_name"
                                value={formData.supplier_name}
                                readOnly
                                placeholder=" "
                                className="ozui-readonly-input"
                            />
                            <label htmlFor="supplier_name">
                                <FontAwesomeIcon icon={faStore} />
                                {translations?.supplierName || 'Supplier Name'}
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations?.phone || 'Phone Number'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="address">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                {translations?.address || 'Address'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper ozui-file-wrapper">
                            <input
                                type="file"
                                id="bill_file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                disabled={saving}
                                className="ozui-file-input"
                            />
                            <label htmlFor="bill_file">
                                <FontAwesomeIcon icon={faUpload} />
                                {translations?.billFile || 'Bill File'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                            <span className="ozui-file-name">{formData.bill_file ? formData.bill_file.name : translations?.noFile || 'No file chosen'}</span>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={productSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="global_product_search"
                                value={globalProductSearchTerm}
                                onChange={(e) => {
                                    setGlobalProductSearchTerm(e.target.value);
                                    searchGlobalProducts(e.target.value);
                                }}
                                onFocus={() => globalProductSearchTerm.length >= 2 && setShowGlobalProductDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="global_product_search">
                                <FontAwesomeIcon icon={faSearch} />
                                {translations?.searchProducts || 'Search Products'}
                            </label>
                            {searchingGlobalProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showGlobalProductDropdown && filteredGlobalProducts.length > 0 && (
                            <div className="ozui-product-dropdown">
                                {filteredGlobalProducts.map(product => (
                                    <div key={product.id} className="ozui-product-item" onClick={() => addProductToItems(product)}>
                                        <div className="ozui-product-item-name">
                                            {product.product_name}
                                            {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                        </div>
                                        <div className="ozui-product-item-details">
                                            {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                            {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                        </div>
                                        <div className="ozui-product-item-price-stock">
                                            <span className="ozui-product-price"><FontAwesomeIcon icon={faRupeeSign} /> {parseFloat(product.sell_price || 0).toFixed(2)}</span>
                                            <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                Stock: {product.quantity} units
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <ItemsTable
                    items={formData.items}
                    onItemChange={handleItemChange}
                    onRemove={removeItem}
                    onAdd={addItem}
                    errors={errors}
                    disabled={saving}
                    translations={translations}
                    isRTL={isRTL}
                    user={user}
                    shopId={currentShop?.shop_id}
                    showToast={showToast}
                />

                {formData.items.length > 0 && (
                    <div className="ozui-form-section">
                        <div className="ozui-calculations">
                            <div className="ozui-calculation-row ozui-grand-total">
                                <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                            </div>
                            <div className={`ozui-calculation-row ${errors.paid ? 'ozui-error' : ''}`}>
                                <span className="ozui-calculation-label">{translations?.paid || 'Paid Amount'}:</span>
                                <div className="ozui-paid-input-wrapper">
                                    <input
                                        type="number"
                                        name="paid"
                                        value={formData.paid}
                                        onChange={handleChange}
                                        step="0.01"
                                        min="0"
                                        max={formData.grand_total}
                                        placeholder="0.00"
                                        disabled={saving}
                                    />
                                </div>
                                {errors.paid && <small>{errors.paid}</small>}
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="ozui-form-actions">

                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={saving || formData.items.length === 0}
                    >
                        {saving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.updatePurchase || 'Update Purchase') : (translations?.savePurchase || 'Save Purchase')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Purchases;